import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import type { RiskLevel, RiskResult } from "./riskScore";

const BORDER = "#E0E4EF";

const LEVEL_PALETTE: Record<RiskLevel, { bg: string; fg: string; scoreFg: string }> = {
  Low: { bg: "#E6F4EA", fg: "#0F5132", scoreFg: "#0F5132" },
  Medium: { bg: "#FEF7E0", fg: "#B7791F", scoreFg: "#B7791F" },
  High: { bg: "#FDECEA", fg: "#B71C1C", scoreFg: "#B71C1C" },
};

const WORKFLOW_RECOMMENDATION: Record<RiskLevel, string> = {
  Low: "Category Manager approval may be sufficient.",
  Medium: "Category Manager + Business Finance, based on commercial conditions.",
  High: "Category Manager + Business Finance + Compliance review.",
};

type Props = {
  result: RiskResult;
};

export function RiskScoreCard({ result }: Props) {
  const palette = LEVEL_PALETTE[result.level];

  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: BORDER, borderRadius: "8px", mb: 2 }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: BORDER,
          bgcolor: "#FAFBFF",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          Risk Assessment
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          px: 2,
          py: 2,
          borderBottom: 1,
          borderColor: BORDER,
        }}
      >
        <Box
          sx={{
            width: 92,
            height: 92,
            borderRadius: "50%",
            bgcolor: palette.bg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 28, fontWeight: 800, color: palette.scoreFg, lineHeight: 1 }}>
            {result.totalScore}
          </Typography>
          <Typography sx={{ fontSize: 10, color: palette.scoreFg, mt: 0.25 }}>
            / 100
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Chip
              size="small"
              label={`${result.level.toUpperCase()} RISK`}
              sx={{
                bgcolor: palette.bg,
                color: palette.fg,
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 0.4,
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
            {WORKFLOW_RECOMMENDATION[result.level]}
          </Typography>
        </Box>
      </Box>

      <Table size="small" sx={{ "& td": { borderColor: BORDER } }}>
        <TableBody>
          {result.components.map((c) => (
            <TableRow key={c.name}>
              <TableCell
                sx={{ width: "35%", color: "text.secondary", fontSize: 13, py: 1.25 }}
              >
                {c.name}
              </TableCell>
              <TableCell sx={{ fontSize: 12, color: "text.secondary", py: 1.25 }}>
                {c.detail}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  width: 64,
                  fontSize: 13,
                  fontWeight: 700,
                  py: 1.25,
                  color: c.score > 0 ? palette.fg : "text.secondary",
                }}
              >
                +{c.score}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
